import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button as MuiButton,
  Dialog,
  Grid,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { DateObject } from "react-multi-date-picker";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { asEntityNumber, buildActivityCenterPath } from "../../../utils/activityCenterUtils";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { RootState } from "../../../redux/store";
import { selectIsValueFoundInToken } from "../../../redux/slice/authSlice";
import { INVOICE_ACTION } from "../../../constants/applicationConstants";
import { AgencyInfo, fetchAgenciesList, fetchAppConfigList, setSingleAlertObj, setSkipLoadingDialog, SingleAlertInfo, useCommonReducer } from "../../../redux/slice/commonSlice";
import { setSkipGlobalLoader } from "../../../service/http";
import { addLateFeeByInvoice, fetchInvoiceAgency, stripeCheckOutPaymentData } from "../../../redux/slice/invoice/InvoiceSlice";
import { fetchDocumentById } from "../../../redux/slice/practiceSlice";
import StripePaymentTypePopup from "../payment/stripe/StripePaymentTypePopup";
import LateFeePopup from "../invoice/LateFeePopup";
import PDFViewer from "../../common/pdf/PDFViewer";
import { addCommission, AddCommissionRequest } from "../../../redux/slice/saleDetails/SaleDetailsSlice";
import {
  fetchManufacturerListByRole,
  useManufacturerListSlice,
} from "../../../redux/slice/manufacturer/ManufacturerSlice";
import { getPracticeListforFilter, usePracticeSlice } from "../../../redux/slice/practiceSlice";
import { editCommissionRateAction } from "../../../redux/slice/order/OrderSlice";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { getPatientListForFilter, usePatientReducer } from "../../../redux/slice/patient/patientSlice";
import { fetchProductsForProductsSearch, useProductListReducer, ProductInfo } from "../../../redux/slice/product/productListSlice";
import { fullName, sortNames } from "../../../utils/Utility";
import CustomMonthYearPicker from "../../common/CustomMonthYearPicker";
import { format } from "date-fns";
import { useNavigation } from "../../../hooks/useNavigation";
import ExcelSheetPopup, { ExcelSheetSelectedCell, ExcelSheetSelectionAction } from "../../common/ExcelSheetPopup";
import { OverridePayoutInvoice, RecordPayoutOverrideForm } from "../invoice/RecordPayoutOverride";
import { AgencyPayoutInvoice, RecordPayoutForm } from "../invoice/RecordPayout";
import EditInvoice, { EditInvoiceHandle } from "../invoice/EditInvoice";
import AddPaymentDetail from "../invoice/AddPaymentDetail";
import RecordCreditMemo from "../invoice/RecordCreditMemo";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EditCommissionPopup from "../orders/EditCommissionPopup";
import CommissionPayoutPopup from "./CommissionPayoutPopup";
import Button from "../../common/Button";
import CustomTextField from "../../common/CustomTextField";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomDateRange from "../../common/CustomDateRange";
import { getV3CommissionTypeDisplayLabel, toCommissionTypeRequestKey } from "../../../utils/Utility";
import { useModalStyles } from "../../common/useModalStyles";
import { useListPageStyles } from "../../common/useListPageStyles";
import {
  ExcelSheetRow,
  parseExcelNumber,
  formatExcelCurrency,
  formatExcelPercent,
} from "../../../utils/excelSheet";
import {
  fetchFinanceHqExcelInvoices,
  resolveFinanceHqExcelPageLimits,
  FINANCE_HQ_EXCEL_MAX_EXPORT_RECORDS,
  applyV3EntityLabelsToFinanceHqColumns,
  FINANCE_HQ_SALES_SHEET_COLUMNS,
  FINANCE_HQ_SALES_SHEET_EXPORT_FILE,
  FINANCE_HQ_SALES_SHEET_SAVABLE_COLUMN_IDS,
  FinanceHqFeeContext,
  FinanceHqPaymentSubRow,
  FinanceHqRateEditContext,
} from "./financeHqSalesSheet";

const FEE_PAYOUT_CACHE_KEY = "financeHqFeePayoutCache";

type FeePayoutCacheEntry = {
  percent: string;
  amount: string;
  status: string;
  date: string;
};

const feePayoutCacheKey = (
  invoiceId: unknown,
  paymentId: unknown,
  feeType: "V3" | "AGENCY" | "OVERRIDE",
  agencyId: unknown
): string => `${invoiceId}|${paymentId}|${feeType}|${agencyId}`;

const readFeePayoutCache = (): Record<string, FeePayoutCacheEntry> => {
  try {
    const raw = window.localStorage.getItem(FEE_PAYOUT_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeFeePayoutCacheEntry = (key: string, entry: FeePayoutCacheEntry) => {
  try {
    const cache = readFeePayoutCache();
    cache[key] = entry;
    window.localStorage.setItem(FEE_PAYOUT_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Best-effort only — a private/blocked localStorage just means no cache hydration.
  }
};

/** Overlays any cached known-good payout values onto freshly fetched rows. */
const hydrateRowsFromPayoutCache = (rows: ExcelSheetRow[]): ExcelSheetRow[] => {
  const cache = readFeePayoutCache();
  if (Object.keys(cache).length === 0) return rows;
  return rows.map((row) => {
    const payments = Array.isArray(row.payments) ? (row.payments as any[]) : [];
    let rowChanged = false;
    const nextPayments = payments.map((subRow: any) => {
      let nextSubRow = subRow;
      const v3Ctx = subRow?._v3Fee || subRow?._v3RateEdit;
      const v3Cached = v3Ctx ? cache[feePayoutCacheKey(v3Ctx.invoiceId, v3Ctx.paymentId, "V3", v3Ctx.agencyId)] : undefined;
      if (v3Cached) {
        nextSubRow = {
          ...nextSubRow,
          v3CommPercent: v3Cached.percent,
          v3NetComm: v3Cached.amount,
          v3CommStatus: v3Cached.status,
          v3ReceivedDate: v3Cached.date,
        };
        rowChanged = true;
      }
      const agencyCtx = subRow?._agencyFee || subRow?._agencyRateEdit;
      const agencyCached = agencyCtx
        ? cache[feePayoutCacheKey(agencyCtx.invoiceId, agencyCtx.paymentId, "AGENCY", agencyCtx.agencyId)]
        : undefined;
      if (agencyCached) {
        nextSubRow = {
          ...nextSubRow,
          agencyCommPercent: agencyCached.percent,
          agencyComm: agencyCached.amount,
          agencyCommStatus: agencyCached.status,
          agencyPaidDate: agencyCached.date,
        };
        rowChanged = true;
      }
      const overrides = Array.isArray(subRow?.overrides) ? subRow.overrides : [];
      let overridesChanged = false;
      const nextOverrides = overrides.map((overrideRow: any) => {
        const ctx = overrideRow?._overrideFee || overrideRow?._overrideRateEdit;
        const cached = ctx ? cache[feePayoutCacheKey(ctx.invoiceId, ctx.paymentId, "OVERRIDE", ctx.agencyId)] : undefined;
        if (!cached) return overrideRow;
        overridesChanged = true;
        return {
          ...overrideRow,
          overrideCommPercent: cached.percent,
          overrideComm: cached.amount,
          overrideCommStatus: cached.status,
          overridePaidDate: cached.date,
        };
      });
      if (overridesChanged) {
        nextSubRow = { ...nextSubRow, overrides: nextOverrides };
        rowChanged = true;
      }
      return nextSubRow;
    });
    return rowChanged ? { ...row, payments: nextPayments } : row;
  });
};

const roundMoney = (value: number): number => Math.max(0, Math.round(value * 100) / 100);

const STATUS_OPTIONS = [
  { value: "PAID", label: "Paid" },
  { value: "PARTIAL", label: "Partial" },
  { value: "UNPAID", label: "Unpaid" },
];

type SheetFilterFormValues = {
  search: string;
  mfgInvoiceNumber: string;
  orderNumber: string;
  invoiceStatus: string;
  mfgIds: string | number;
  practiceId: string | number;
  patientId: string | number;
  productId: string | number;
  agencyId: string | number;
  invoiceDateRange: string;
  dueDateRange: string;
  v3CommStatus: string;
  agencyCommStatus: string;
  ovrCommStatus: string;
  v3ReceivedDateRange: string;
  agencyPaidDateRange: string;
  ovrPaidDateRange: string;
  commPeriod: string;
  serialNumber: string;
  usageStatus: string;
};

const FILTER_DEFAULT_VALUES: SheetFilterFormValues = {
  search: "",
  mfgInvoiceNumber: "",
  orderNumber: "",
  invoiceStatus: "",
  mfgIds: "",
  practiceId: "",
  patientId: "",
  productId: "",
  agencyId: "",
  invoiceDateRange: "",
  dueDateRange: "",
  v3CommStatus: "",
  agencyCommStatus: "",
  ovrCommStatus: "",
  v3ReceivedDateRange: "",
  agencyPaidDateRange: "",
  ovrPaidDateRange: "",
  commPeriod: "",
  serialNumber: "",
  usageStatus: "",
};

type SheetFilters = SheetFilterFormValues & {
  invoiceDateFrom: string;
  invoiceDateTo: string;
  dueDateFrom: string;
  dueDateTo: string;
  v3ReceivedFromDate: string;
  v3ReceivedToDate: string;
  agencyPaidFromDate: string;
  agencyPaidToDate: string;
  ovrPaidFromDate: string;
  ovrPaidToDate: string;
};

const DEFAULT_FILTERS: SheetFilters = {
  ...FILTER_DEFAULT_VALUES,
  invoiceDateFrom: "",
  invoiceDateTo: "",
  dueDateFrom: "",
  dueDateTo: "",
  v3ReceivedFromDate: "",
  v3ReceivedToDate: "",
  agencyPaidFromDate: "",
  agencyPaidToDate: "",
  ovrPaidFromDate: "",
  ovrPaidToDate: "",
};


const FinanceHqSalesSheetPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const canAccessActivityCenter = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.ACTIVITY_CENTER)(state));
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const financeHqSalesSheetColumns = useMemo(
    () =>
      applyV3EntityLabelsToFinanceHqColumns(
        FINANCE_HQ_SALES_SHEET_COLUMNS,
        getV3CommissionTypeDisplayLabel(allCodeTablesValuesMap)
      ),
    [allCodeTablesValuesMap]
  );
  const { navigate } = useNavigation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { tokens, paperSx, headerSx, iconBoxSx, bodySx, footerSx, closeIconSx, primaryBtnSx, cancelBtnSx } =
    useModalStyles();
  const { filterPanelSx, primaryActionBtnSx, outlineBtnSx } = useListPageStyles();
  const quickEditInvoiceRef = useRef<EditInvoiceHandle>(null);
  const [quickEditInvoiceId, setQuickEditInvoiceId] = useState<number | null>(null);
  const [quickEditHighlightField, setQuickEditHighlightField] = useState<string | undefined>(undefined);
  const [recordPaymentContext, setRecordPaymentContext] = useState<{
    invoiceId: number;
    paymentDate?: string;
    paidAmount?: string;
    highlightField?: string;
  } | null>(null);
  const [creditMemoContext, setCreditMemoContext] = useState<{
    invoiceId: number;
    invNumber?: string;
    invDate?: string;
    practice?: string;
    agency?: string;
    mfr?: string;
  } | null>(null);
  const [stripePaymentInvoice, setStripePaymentInvoice] = useState<ExcelSheetRow | null>(null);
  const [pdfContent, setPdfContent] = useState<string>("");
  const [showPaymentInstructionPdf, setShowPaymentInstructionPdf] = useState<{
    noteName: string;
    fileName: string;
  } | null>(null);
  const [lateFeeContext, setLateFeeContext] = useState<{ invoiceId: number; lateFee: number } | null>(null);
  const [view, setView] = useState<{
    loading: boolean;
    loadMoreLoading: boolean;
    rows: ExcelSheetRow[];
    totalRecords: number;
    summary?: Record<string, number>;
  }>({ loading: true, loadMoreLoading: false, rows: [], totalRecords: 0 });
  const requestIdRef = useRef(0);
  const [commissionRateEdit, setCommissionRateEdit] = useState<{
    label: string;
    invoiceId: number;
    columnId: "agencyCommPercent" | "overrideCommPercent" | "v3CommPercent";
    agencyId: number;
    /** "rate": editing via the % column (rate drives, amount is read-only).
     *  "amount": editing via the COMM column (amount drives, rate is read-only). */
    editMode: "rate" | "amount";
    paymentData: { paymentId: number; invoiceAgencyId: number; currentRate: number };
    agencyData: { agencyName: string; invoiceAmt: number; invoiceNumber?: string; orderNumber?: string };
  } | null>(null);
  const [isRefreshingAfterSave, setIsRefreshingAfterSave] = useState(false);
  const [highlightInvoiceId, setHighlightInvoiceId] = useState<number | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const [feePayout, setFeePayout] = useState<{
    label: string;
    context: FinanceHqFeeContext;
    commissionObject: AddCommissionRequest;
    expectedAmount: number;
    alreadyPaidAmount: number;
    invoiceNumber?: string;
    orderNumber?: string;
  } | null>(null);
  const [overridePayoutInvoices, setOverridePayoutInvoices] = useState<OverridePayoutInvoice[] | null>(null);
  const [agencyPayoutInvoices, setAgencyPayoutInvoices] = useState<AgencyPayoutInvoice[] | null>(null);
  const { control, getValues, setValue: setFilterValue, reset: resetFilterForm } = useForm<SheetFilterFormValues>({
    defaultValues: FILTER_DEFAULT_VALUES,
  });
  const [dateFilters, setDateFilters] = useState({
    invoiceDateFrom: "",
    invoiceDateTo: "",
    dueDateFrom: "",
    dueDateTo: "",
    v3ReceivedFromDate: "",
    v3ReceivedToDate: "",
    agencyPaidFromDate: "",
    agencyPaidToDate: "",
    ovrPaidFromDate: "",
    ovrPaidToDate: "",
  });
  const [appliedFilters, setAppliedFilters] = useState<SheetFilters>(DEFAULT_FILTERS);
  const [filterSearchTick, setFilterSearchTick] = useState(0);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(null);
  const filtersOpen = Boolean(filtersAnchorEl);
  const { manufacturerInfoListByUserRole } = useManufacturerListSlice();
  const { practiceByUserRole } = usePracticeSlice();
  const { patientsByUserRole } = usePatientReducer();
  const { productsByUserRole } = useProductListReducer();
  const { agencyInfoList, appConfigList } = useCommonReducer();
  const appConfigListRef = useRef(appConfigList);
  appConfigListRef.current = appConfigList;
  const { initialLimit: excelInitialLimit, loadMoreLimit: excelLoadMoreLimit } = useMemo(
    () => resolveFinanceHqExcelPageLimits(appConfigList),
    [appConfigList]
  );
  const manufacturerOptions = useMemo(
    () => [
      { value: "ALL", label: "All" },
      ...(manufacturerInfoListByUserRole || []).map((item: any) => ({
        value: item.manufacturerId,
        label: item.manufacturerName,
      })),
    ],
    [manufacturerInfoListByUserRole]
  );
  const practiceOptions = useMemo(
    () => [
      { value: "ALL", label: "All" },
      ...(practiceByUserRole || []).map((item: any) => ({
        value: item.practiceId,
        label: item.practiceName,
      })),
    ],
    [practiceByUserRole]
  );
  const patientOptions = useMemo(
    () => [
      { value: "ALL", label: "All" },
      ...(patientsByUserRole || []).map((option: any) => ({
        value: option.patientId,
        label: `${fullName("", option.firstName, option.middleName, option.lastName)} - ${option.birthDate}`,
      })),
    ],
    [patientsByUserRole]
  );
  const productOptions = useMemo(
    () => [
      { value: "ALL", label: "All" },
      ...sortNames(productsByUserRole || [], "productName").map((option: ProductInfo) => ({
        value: Number(option.productId),
        label: option.productName,
      })),
    ],
    [productsByUserRole]
  );
  const agencyOptions = useMemo(
    () => [
      { value: "ALL", label: "All" },
      ...sortNames(agencyInfoList || [], "agencyName").map((item: AgencyInfo) => ({
        value: item.agencyId,
        label: item.agencyName,
      })),
    ],
    [agencyInfoList]
  );
  const statusDropdownOptions = useMemo(() => [{ value: "ALL", label: "All" }, ...STATUS_OPTIONS], []);
  const v3PayStatusOptions = useMemo(
    () => [
      { value: "ALL", label: "All" },
      { value: "UNPAID", label: "Not Received" },
      { value: "PAID", label: "Received" },
    ],
    []
  );
  const agyPayStatusOptions = useMemo(
    () => [
      { value: "ALL", label: "All" },
      { value: "UNPAID", label: "Unpaid" },
      { value: "PAID", label: "Paid" },
    ],
    []
  );
  const usageStatusDropdownOptions = useMemo(
    () => [
      { value: "ALL", label: "All" },
      { value: "AVAILABLE", label: "Available" },
      { value: "APPLIED", label: "Applied" },
      { value: "RETURN_INITIATED", label: "Return Initiated" },
      { value: "RETURN_PENDING", label: "Return Pending" },
      { value: "RETURNED", label: "Returned" },
    ],
    []
  );

  useLayoutEffect(() => {
    setSkipGlobalLoader(true);
    dispatch(setSkipLoadingDialog(true));
    return () => {
      setSkipGlobalLoader(false);
      dispatch(setSkipLoadingDialog(false));
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchManufacturerListByRole({ skipLoader: true }));
    dispatch(getPracticeListforFilter({ skipLoader: true }));
    dispatch(getPatientListForFilter({ skipLoader: true }));
    dispatch(fetchProductsForProductsSearch({ includeProductPrice: false, skipLoader: true }));
    dispatch(fetchAgenciesList({ skipLoader: true }));
  }, [dispatch]);

  const handleInvoiceDateRangeChange = (fromDate: DateObject | null, toDate: DateObject | null) => {
    setDateFilters((prev) => ({
      ...prev,
      invoiceDateFrom: fromDate ? fromDate.format("MM/DD/YYYY") : "",
      invoiceDateTo: toDate ? toDate.format("MM/DD/YYYY") : "",
    }));
  };

  const handleDueDateRangeChange = (fromDate: DateObject | null, toDate: DateObject | null) => {
    setDateFilters((prev) => ({
      ...prev,
      dueDateFrom: fromDate ? fromDate.format("MM/DD/YYYY") : "",
      dueDateTo: toDate ? toDate.format("MM/DD/YYYY") : "",
    }));
  };

  const handleV3ReceivedDateRangeChange = (fromDate: DateObject | null, toDate: DateObject | null) => {
    setDateFilters((prev) => ({
      ...prev,
      v3ReceivedFromDate: fromDate ? fromDate.format("MM/DD/YYYY") : "",
      v3ReceivedToDate: toDate ? toDate.format("MM/DD/YYYY") : "",
    }));
  };

  const handleAgencyPaidDateRangeChange = (fromDate: DateObject | null, toDate: DateObject | null) => {
    setDateFilters((prev) => ({
      ...prev,
      agencyPaidFromDate: fromDate ? fromDate.format("MM/DD/YYYY") : "",
      agencyPaidToDate: toDate ? toDate.format("MM/DD/YYYY") : "",
    }));
  };

  const handleOvrPaidDateRangeChange = (fromDate: DateObject | null, toDate: DateObject | null) => {
    setDateFilters((prev) => ({
      ...prev,
      ovrPaidFromDate: fromDate ? fromDate.format("MM/DD/YYYY") : "",
      ovrPaidToDate: toDate ? toDate.format("MM/DD/YYYY") : "",
    }));
  };

  const handleCommPeriodChange = (date: Date | null, endDate?: Date | null) => {
    if (date && endDate) {
      setFilterValue("commPeriod", `${format(date, "MMM yy")} - ${format(endDate, "MMM yy")}`);
    } else if (date && !endDate) {
      setFilterValue("commPeriod", format(date, "MMM yy"));
    }
  };

  const handleClearCommPeriod = () => setFilterValue("commPeriod", "");

  const isAllOrEmpty = (value: string | number) => value === "ALL" || value === "" || value === null || value === undefined;

  const serverFilterParams = (source: SheetFilters) => ({
    invoiceStatus: !isAllOrEmpty(source.invoiceStatus) ? [source.invoiceStatus] : undefined,
    manufacturerIds: !isAllOrEmpty(source.mfgIds) ? [Number(source.mfgIds)] : undefined,
    practiceIds: !isAllOrEmpty(source.practiceId) ? [Number(source.practiceId)] : undefined,
    patientIds: !isAllOrEmpty(source.patientId) ? [Number(source.patientId)] : undefined,
    productIds: !isAllOrEmpty(source.productId) ? [Number(source.productId)] : undefined,
    agencyIds: !isAllOrEmpty(source.agencyId) ? [Number(source.agencyId)] : undefined,
    mfgInvoiceNumber: source.mfgInvoiceNumber || undefined,
    orderNumber: source.orderNumber || undefined,
    serialNumber: source.serialNumber || undefined,
    usageStatus: !isAllOrEmpty(source.usageStatus) ? source.usageStatus : undefined,
    v3CommStatus: !isAllOrEmpty(source.v3CommStatus) ? source.v3CommStatus : undefined,
    agencyCommStatus: !isAllOrEmpty(source.agencyCommStatus) ? source.agencyCommStatus : undefined,
    ovrCommStatus: !isAllOrEmpty(source.ovrCommStatus) ? source.ovrCommStatus : undefined,
    commPeriod: source.commPeriod || undefined,
    invoiceDateFrom: source.invoiceDateFrom,
    invoiceDateTo: source.invoiceDateTo,
    v3ReceivedFromDate: source.v3ReceivedFromDate,
    v3ReceivedToDate: source.v3ReceivedToDate,
    agencyPaidFromDate: source.agencyPaidFromDate,
    agencyPaidToDate: source.agencyPaidToDate,
    ovrPaidFromDate: source.ovrPaidFromDate,
    ovrPaidToDate: source.ovrPaidToDate,
  });

  useEffect(() => {
    let cancelled = false;
    const requestId = ++requestIdRef.current;

    const load = async () => {
      // Startup skips getAppConfigList once applicationVersion is stored, so this
      // page would otherwise keep the 250 fallback even when Config List has a value.
      let list = appConfigListRef.current;
      try {
        const action: any = await dispatch(fetchAppConfigList());
        if (Array.isArray(action?.payload)) {
          list = action.payload;
        }
      } catch {
        // Use whatever is already in the store.
      }
      if (cancelled || requestIdRef.current !== requestId) return;
      const { initialLimit } = resolveFinanceHqExcelPageLimits(list);
      try {
        const page = await fetchFinanceHqExcelInvoices({ offset: 0, limit: initialLimit });
        if (cancelled || requestIdRef.current !== requestId) return;
        setView((prev) => ({
          ...prev,
          loading: false,
          rows: hydrateRowsFromPayoutCache(page.rows),
          totalRecords: page.totalElements,
          summary: page.summary,
        }));
      } catch {
        if (cancelled || requestIdRef.current !== requestId) return;
        setView((prev) => ({ ...prev, loading: false }));
        const errObj: SingleAlertInfo = {
          message: "Unable to load invoices for the sales sheet.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(errObj));
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const handleLoadMore = async () => {
    const requestId = requestIdRef.current;
    const offset = view.rows.length;
    setView((prev) => ({ ...prev, loadMoreLoading: true }));
    try {
      const page = await fetchFinanceHqExcelInvoices({
        offset,
        limit: excelLoadMoreLimit,
        ...serverFilterParams(appliedFilters),
      });
      if (requestIdRef.current !== requestId) return;
      setView((prev) => ({
        ...prev,
        loadMoreLoading: false,
        rows: [...prev.rows, ...hydrateRowsFromPayoutCache(page.rows)],
        totalRecords: page.totalElements || prev.totalRecords,
        summary: page.summary ?? prev.summary,
      }));
    } catch {
      if (requestIdRef.current !== requestId) return;
      const errObj: SingleAlertInfo = {
        message: "Unable to load more invoices.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(errObj));
      setView((prev) => ({ ...prev, loadMoreLoading: false }));
    }
  };

  const handleSaveRow = async ({
    row,
    changedFields,
  }: {
    rowIndex: number;
    row: ExcelSheetRow;
    original: ExcelSheetRow;
    changedFields: Record<string, unknown>;
  }) => {
    if (!("lateFee" in changedFields)) return;
    const invoiceId = row.invoiceId;
    if (invoiceId === undefined || invoiceId === null || invoiceId === "") {
      throw new Error("Missing invoice id");
    }
    try {
      await dispatch(
        addLateFeeByInvoice({
          invoiceId: invoiceId as string | number,
          lateFee: parseExcelNumber(changedFields.lateFee),
          skipLoader: true,
        })
      ).unwrap();
      dispatch(
        setSingleAlertObj({
          message: `Late fee updated for invoice ${row.mfgInvoiceNumber || invoiceId}`,
          alertType: "success",
        })
      );
    } catch (error) {
      dispatch(
        setSingleAlertObj({
          message: `Unable to save late fee for invoice ${row.mfgInvoiceNumber || invoiceId}`,
          alertType: "error",
        })
      );
      throw error;
    }
  };

  const QUICK_EDIT_HIGHLIGHT_FIELDS: Record<string, string> = {
    mfgInvoiceDate: "invoicedDate",
    invoiceDueDate: "invoiceDueDate",
    shippingCharges: "shippingCharge",
    lateFee: "lateFee",
  };
  const RECORD_PAYMENT_HIGHLIGHT_FIELDS: Record<string, string> = {
    paymentAmount: "paidAmount",
    paymentDate: "paymentDate",
  };

  const handleCellPopupEdit = (
    row: ExcelSheetRow,
    _rowIndex: number,
    columnId: string,
    subRow?: Record<string, unknown>
  ) => {
    const invoiceId = row.invoiceId;
    if (invoiceId === undefined || invoiceId === null || invoiceId === "") return;
    const numericId = Number(invoiceId);
    if (!Number.isFinite(numericId)) return;
    if (columnId === "paymentAmount" || columnId === "paymentDate") {
      const paymentDue = Number(row.paymentDue ?? 0);
      const isAddingDuePayment = columnId === "paymentAmount" && paymentDue > 0.004;
      setRecordPaymentContext({
        invoiceId: numericId,
        paymentDate: isAddingDuePayment ? "" : String(subRow?.paymentDate ?? ""),
        paidAmount: isAddingDuePayment ? "" : String(subRow?.paymentAmount ?? ""),
        highlightField: RECORD_PAYMENT_HIGHLIGHT_FIELDS[columnId],
      });
      return;
    }
    setQuickEditHighlightField(QUICK_EDIT_HIGHLIGHT_FIELDS[columnId]);
    setQuickEditInvoiceId(numericId);
  };

  const handleCloseQuickEditInvoice = () => {
    setQuickEditInvoiceId(null);
    setQuickEditHighlightField(undefined);
  };

  const handleCloseRecordPayment = () => setRecordPaymentContext(null);

  const handleRecordPaymentSaved = () =>
    refreshSheet(
      recordPaymentContext?.invoiceId ?? null,
      "Payment was recorded, but the sales sheet could not be refreshed. Reopen it to see the latest data."
    );

  const handleCloseStripePayment = () => setStripePaymentInvoice(null);

  const handleStripeCheckout = async (
    invoiceId: number,
    paymentMethodType: string,
    paymentType: string,
    paymentAmount: number
  ) => {
    const data = {
      returnPath: location.pathname + location.search,
      invoiceId,
      paymentMethod: paymentMethodType,
      invoiceLineItemList: [{ invoiceId, paymentAmt: paymentAmount, paymentType }],
    };
    await dispatch(stripeCheckOutPaymentData({ ...data, skipLoader: true }))
      .then((res: any) => {
        if (res?.payload?.errors?.length > 0) {
          dispatch(
            setSingleAlertObj({ message: res.payload.errors[0].errorMessage, alertType: "error" })
          );
        } else if (res?.payload?.data?.checkoutUrl) {
          window.location.href = res.payload.data.checkoutUrl;
        }
      })
      .catch(() => {});
  };

  const handleOpenPaymentInstructionDoc = async (row: ExcelSheetRow) => {
    const actionResult: any = await dispatch(
      fetchDocumentById({ documentId: String(row.paymentInstructionValue ?? ""), skipLoader: true })
    );
    const documentData = actionResult?.payload;
    if (documentData?.content) {
      setPdfContent(documentData.content);
      setShowPaymentInstructionPdf({
        noteName: `Payment Instruction - ${row.manufacturerName}`,
        fileName: `Payment Instruction_${row.manufacturerName}`,
      });
    }
  };

  const handlePayInvoiceAction = (row: ExcelSheetRow) => {
    const stripeConfig = row.stripeConfig as { stripe_integration?: string } | undefined;
    const paymentDue = Number(row.paymentDue ?? 0);
    if (stripeConfig?.stripe_integration === "Y") {
      if (paymentDue > 0) {
        setStripePaymentInvoice(row);
      } else {
        dispatch(
          setSingleAlertObj({
            message:
              "This invoice currently shows 'Unpaid,' but the outstanding amount due is 0. It was likely settled by credits or adjustments, so no payment is needed.",
            alertType: "error",
          })
        );
      }
      return;
    }
    if (row.hasPaymentInstruction) {
      const value = String(row.paymentInstructionValue ?? "");
      if (row.paymentInstructionType === "Product Document" && value) {
        if (value.startsWith("https://") || value.startsWith("http://")) {
          window.open(value, "_blank");
        } else {
          handleOpenPaymentInstructionDoc(row);
        }
      } else if (row.paymentInstructionType === "External URL" && value) {
        window.open(value, "_blank");
      }
      return;
    }
    dispatch(
      setSingleAlertObj({ message: "No payment method is configured for this invoice.", alertType: "error" })
    );
  };

  const handleInvoiceAuditAction = (row: ExcelSheetRow) => {
    window.open(`/auditlog?entityName=INVOICE&entityNumber=${row.mfgInvoiceNumber}`, "_blank");
  };

  const handleDetailAction = (row: ExcelSheetRow) => {
    if (row.invoiceId == null || row.invoiceId === "") return;
    const params = new URLSearchParams({ invoiceId: String(row.invoiceId) });
    const orderNumber = asEntityNumber(row.raworderNumber) || asEntityNumber(row.orderNumber);
    if (orderNumber) params.set("orderNumber", orderNumber);
    if (row.invoiceStatus) params.set("invStatus", String(row.invoiceStatus));
    window.open(`/commission_details?${params.toString()}`, "_blank");
  };

  const handleActivityCenterAction = (row: ExcelSheetRow) => {
    window.open(
      buildActivityCenterPath({
        tab: "invoice",
        invoiceId: Number(row.invoiceId),
        invoiceNumber: asEntityNumber(row.mfgInvoiceNumber),
        orderId: Number(row.orderId) || undefined,
        orderNumber: asEntityNumber(row.raworderNumber) || asEntityNumber(row.orderNumber),
      }),
      "_blank"
    );
  };

  const handleRecordCreditMemoAction = (row: ExcelSheetRow) => {
    const invoiceId = Number(row.invoiceId);
    if (!Number.isFinite(invoiceId)) return;
    setCreditMemoContext({
      invoiceId,
      invNumber: row.mfgInvoiceNumber !== undefined ? String(row.mfgInvoiceNumber) : undefined,
      invDate: row.mfgInvoiceDate !== undefined ? String(row.mfgInvoiceDate) : undefined,
      practice: row.practiceName !== undefined ? String(row.practiceName) : undefined,
      agency: row.agencyName !== undefined ? String(row.agencyName) : undefined,
      mfr: row.manufacturerName !== undefined ? String(row.manufacturerName) : undefined,
    });
  };

  const handleCloseCreditMemo = () => setCreditMemoContext(null);

  const handleCreditMemoSaved = () =>
    refreshSheet(
      creditMemoContext?.invoiceId ?? null,
      "Credit memo was recorded, but the sales sheet could not be refreshed. Reopen it to see the latest data."
    );

  const handleRecordPaymentAction = (row: ExcelSheetRow) => {
    const invoiceId = Number(row.invoiceId);
    if (!Number.isFinite(invoiceId)) return;
    setRecordPaymentContext({ invoiceId, paymentDate: "", paidAmount: "" });
  };

  const handleCloseLateFee = () => setLateFeeContext(null);

  const handleConfirmLateFee = (data: { lateFee: string }) => {
    if (!lateFeeContext) return;
    const { invoiceId } = lateFeeContext;
    dispatch(
      addLateFeeByInvoice({ invoiceId, lateFee: parseExcelNumber(data.lateFee), skipLoader: true })
    ).then(() => {
      refreshSheet(
        invoiceId,
        "Late fee was recorded, but the sales sheet could not be refreshed. Reopen it to see the latest data."
      );
    });
    setLateFeeContext(null);
  };

  const getRowActions = (row: ExcelSheetRow) => {
    const invoiceId = Number(row.invoiceId);
    if (!Number.isFinite(invoiceId)) return [];
    return [
      {
        key: "detail",
        label: "Detail",
        icon: <ListAltOutlinedIcon sx={{ fontSize: 15 }} />,
        iconColor: "#0288d1",
        onClick: () => handleDetailAction(row),
      },
      ...(canAccessActivityCenter
        ? [
            {
              key: "activityCenter",
              label: "Activity Center",
              icon: <HistoryOutlinedIcon sx={{ fontSize: 15 }} />,
              iconColor: "#0288d1",
              onClick: () => handleActivityCenterAction(row),
            },
          ]
        : []),
      {
        key: "creditMemo",
        label: "Record Credit Memo",
        icon: <CreditCardIcon sx={{ fontSize: 15 }} />,
        iconColor: "#ed6c02",
        onClick: () => handleRecordCreditMemoAction(row),
      },
      {
        key: "recordPayment",
        label: "Record Payment",
        icon: <AttachMoneyIcon sx={{ fontSize: 15 }} />,
        iconColor: "#2e7d32",
        onClick: () => handleRecordPaymentAction(row),
      },
      {
        key: "payViaStripe",
        label: "Pay via Stripe",
        icon: <AttachMoneyIcon sx={{ fontSize: 15 }} />,
        iconColor: "#ffffff",
        iconBackground: "linear-gradient(135deg, #4f46e5, #7c3aed, #9333ea, #db2777)",
        onClick: () => handlePayInvoiceAction(row),
      },
      {
        key: "lateFee",
        label: "Late Fee",
        icon: <CurrencyExchangeOutlinedIcon sx={{ fontSize: 15 }} />,
        iconColor: "#2e7d32",
        onClick: () => setLateFeeContext({ invoiceId, lateFee: Number(row.lateFee) || 0 }),
      },
      {
        key: "invoiceAudit",
        label: "Invoice Audit",
        icon: <TrackChangesIcon sx={{ fontSize: 15 }} />,
        iconColor: "#ed6c02",
        onClick: () => handleInvoiceAuditAction(row),
      },
    ];
  };

  const OVERRIDE_COLUMN_IDS = new Set(["overrideCommPercent", "overrideComm", "overrideCommStatus", "overridePaidDate"]);
  const AGENCY_COLUMN_IDS = new Set(["agencyCommPercent", "agencyComm", "agencyCommStatus", "agencyPaidDate"]);

  const rowPayments = (row: ExcelSheetRow) =>
    (row.payments as FinanceHqPaymentSubRow[] | undefined) ?? [];

  const rowOverrides = (row: ExcelSheetRow) =>
    rowPayments(row).flatMap((payment) => payment.overrides ?? []);

  const toOverridePayoutInvoice = (row: ExcelSheetRow): OverridePayoutInvoice => ({
    invoiceId: Number(row.invoiceId),
    mfgInvoiceNumber: String(row.mfgInvoiceNumber ?? ""),
    mfgInvoiceDate: String(row.mfgInvoiceDate ?? ""),
    invoiceAmount: parseExcelNumber(row.netInvoiceAmount),
    invoiceStatus: String(row.invoiceStatus ?? ""),
    overrideAgencyName: rowOverrides(row).find((override) => override.overrideAgencyName)?.overrideAgencyName,
  });

  const toAgencyPayoutInvoice = (row: ExcelSheetRow): AgencyPayoutInvoice => ({
    invoiceId: Number(row.invoiceId),
    mfgInvoiceNumber: String(row.mfgInvoiceNumber ?? ""),
    mfgInvoiceDate: String(row.mfgInvoiceDate ?? ""),
    invoiceAmount: parseExcelNumber(row.netInvoiceAmount),
    invoiceStatus: String(row.invoiceStatus ?? ""),
    agencyName:
      rowPayments(row).find((payment) => payment._agencyRateEdit?.agencyName)?._agencyRateEdit?.agencyName ||
      (row.agencyName ? String(row.agencyName) : undefined),
  });

  const payoutCountLabel = (prefix: string, count: number) =>
    `${prefix} (${count} ${count === 1 ? "invoice" : "invoices"})`;

  /** Right-click on selected Agency or Override cells → pay those service fees in one payout. */
  const getSelectionActions = (cells: ExcelSheetSelectedCell[]): ExcelSheetSelectionAction[] => {
    const agencyRows = new Map<number, ExcelSheetRow>();
    const overrideRows = new Map<number, ExcelSheetRow>();
    cells.forEach(({ row, columnId }) => {
      const invoiceId = Number(row.invoiceId);
      if (!Number.isFinite(invoiceId)) return;
      if (AGENCY_COLUMN_IDS.has(columnId) && !agencyRows.has(invoiceId) && rowPayments(row).some((payment) => payment._agencyFee)) {
        agencyRows.set(invoiceId, row);
      }
      if (OVERRIDE_COLUMN_IDS.has(columnId) && !overrideRows.has(invoiceId) && rowOverrides(row).some((override) => override._overrideFee)) {
        overrideRows.set(invoiceId, row);
      }
    });
    const actions: ExcelSheetSelectionAction[] = [];
    if (agencyRows.size > 0) {
      actions.push({
        key: "createAgencyPayout",
        label: payoutCountLabel("Create AGY Pmts", agencyRows.size),
        icon: <PaymentsOutlinedIcon sx={{ fontSize: 16 }} />,
        iconColor: "#0288d1",
        onClick: () => setAgencyPayoutInvoices(Array.from(agencyRows.values()).map(toAgencyPayoutInvoice)),
      });
    }
    if (overrideRows.size > 0) {
      actions.push({
        key: "createOverridePayout",
        label: payoutCountLabel("Create OVR Pmts", overrideRows.size),
        icon: <PaymentsOutlinedIcon sx={{ fontSize: 16 }} />,
        onClick: () => setOverridePayoutInvoices(Array.from(overrideRows.values()).map(toOverridePayoutInvoice)),
      });
    }
    return actions;
  };

  const handleCloseOverridePayout = () => setOverridePayoutInvoices(null);

  const handleOverridePayoutRecorded = () => {
    const firstInvoiceId = overridePayoutInvoices?.[0]?.invoiceId ?? null;
    setOverridePayoutInvoices(null);
    refreshSheet(
      firstInvoiceId,
      "Override payout was recorded, but the sales sheet could not be refreshed. Reopen it to see the latest data."
    );
  };

  const handleCloseAgencyPayout = () => setAgencyPayoutInvoices(null);

  const handleAgencyPayoutRecorded = () => {
    const firstInvoiceId = agencyPayoutInvoices?.[0]?.invoiceId ?? null;
    setAgencyPayoutInvoices(null);
    refreshSheet(
      firstInvoiceId,
      "Agency payout was recorded, but the sales sheet could not be refreshed. Reopen it to see the latest data."
    );
  };

  const runFilteredFetch = async (source: SheetFilters) => {
    const requestId = ++requestIdRef.current;
    setView((prev) => ({ ...prev, loading: true }));
    try {
      const page = await fetchFinanceHqExcelInvoices({
        offset: 0,
        limit: excelInitialLimit,
        ...serverFilterParams(source),
      });
      if (requestIdRef.current !== requestId) return;
      setView({
        loading: false,
        loadMoreLoading: false,
        rows: hydrateRowsFromPayoutCache(page.rows),
        totalRecords: page.totalElements,
        summary: page.summary,
      });
    } catch {
      if (requestIdRef.current !== requestId) return;
      setView((prev) => ({ ...prev, loading: false }));
      dispatch(setSingleAlertObj({ message: "Unable to load invoices for the sales sheet.", alertType: "error" }));
    }
  };

  const handleSearchFilters = () => {
    const values = getValues();
    const practiceId = isAllOrEmpty(values.practiceId) ? "ALL" : values.practiceId;
    const mfgIds = isAllOrEmpty(values.mfgIds) ? "ALL" : values.mfgIds;
    const patientId = isAllOrEmpty(values.patientId) ? "ALL" : values.patientId;
    const productId = isAllOrEmpty(values.productId) ? "ALL" : values.productId;
    const agencyId = isAllOrEmpty(values.agencyId) ? "ALL" : values.agencyId;
    const invoiceStatus = isAllOrEmpty(values.invoiceStatus) ? "ALL" : values.invoiceStatus;
    const v3CommStatus = isAllOrEmpty(values.v3CommStatus) ? "ALL" : values.v3CommStatus;
    const agencyCommStatus = isAllOrEmpty(values.agencyCommStatus) ? "ALL" : values.agencyCommStatus;
    const ovrCommStatus = isAllOrEmpty(values.ovrCommStatus) ? "ALL" : values.ovrCommStatus;
    const usageStatus = isAllOrEmpty(values.usageStatus) ? "ALL" : values.usageStatus;

    const next: SheetFilters = {
      ...values,
      practiceId,
      mfgIds,
      patientId,
      productId,
      agencyId,
      invoiceStatus,
      v3CommStatus,
      agencyCommStatus,
      ovrCommStatus,
      usageStatus,
      ...dateFilters,
    };
    setAppliedFilters(next);
    runFilteredFetch(next);
    setFilterSearchTick((n) => n + 1);
  };

  const handleClearFilters = () => {
    resetFilterForm(FILTER_DEFAULT_VALUES);
    setDateFilters({
      invoiceDateFrom: "",
      invoiceDateTo: "",
      dueDateFrom: "",
      dueDateTo: "",
      v3ReceivedFromDate: "",
      v3ReceivedToDate: "",
      agencyPaidFromDate: "",
      agencyPaidToDate: "",
      ovrPaidFromDate: "",
      ovrPaidToDate: "",
    });
    setAppliedFilters(DEFAULT_FILTERS);
    runFilteredFetch(DEFAULT_FILTERS);
  };

  const applyClientSideFilters = (rows: ExcelSheetRow[], filters: SheetFilters): ExcelSheetRow[] => {
    const search = filters.search.trim().toLowerCase();
    const dueFrom = filters.dueDateFrom ? dayjs(filters.dueDateFrom, "MM/DD/YYYY") : null;
    const dueTo = filters.dueDateTo ? dayjs(filters.dueDateTo, "MM/DD/YYYY") : null;
    if (!search && !dueFrom && !dueTo) return rows;
    return rows.filter((row) => {
      if (search) {
        const haystack = [row.mfgInvoiceNumber, row.orderNumber, row.practiceName, row.manufacturerName]
          .map((v) => String(v ?? "").toLowerCase())
          .join(" ");
        if (!haystack.includes(search)) return false;
      }
      if (dueFrom || dueTo) {
        const rowDue = row.invoiceDueDate ? dayjs(String(row.invoiceDueDate), "MM/DD/YYYY") : null;
        if (!rowDue || !rowDue.isValid()) return false;
        if (dueFrom && rowDue.isBefore(dueFrom, "day")) return false;
        if (dueTo && rowDue.isAfter(dueTo, "day")) return false;
      }
      return true;
    });
  };

  const displayRows = useMemo(
    () => applyClientSideFilters(view.rows, appliedFilters),
    [view.rows, appliedFilters]
  );

  const handleFetchAllRows = async (): Promise<ExcelSheetRow[]> => {
    const limit = Math.min(
      FINANCE_HQ_EXCEL_MAX_EXPORT_RECORDS,
      Math.max(view.totalRecords, view.rows.length, excelInitialLimit)
    );
    const page = await fetchFinanceHqExcelInvoices({ offset: 0, limit, ...serverFilterParams(appliedFilters) });
    return applyClientSideFilters(hydrateRowsFromPayoutCache(page.rows), appliedFilters).slice(
      0,
      FINANCE_HQ_EXCEL_MAX_EXPORT_RECORDS
    );
  };

  const refreshSheet = async (highlightId: number | null, failureMessage: string) => {
    const requestId = ++requestIdRef.current;
    setIsRefreshingAfterSave(true);
    try {
      const limit = Math.max(view.rows.length, excelInitialLimit);
      const page = await fetchFinanceHqExcelInvoices({ offset: 0, limit, ...serverFilterParams(appliedFilters) });
      if (requestIdRef.current !== requestId) return;
      setView((prev) => ({
        ...prev,
        rows: hydrateRowsFromPayoutCache(page.rows),
        totalRecords: page.totalElements || prev.totalRecords,
        summary: page.summary ?? prev.summary,
      }));
      dispatch(setSingleAlertObj({ message: "Finance HQ sales sheet updated.", alertType: "success" }));
      if (highlightId !== null) {
        setHighlightInvoiceId(highlightId);
        if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = window.setTimeout(() => setHighlightInvoiceId(null), 2500);
      }
    } catch {
      if (requestIdRef.current !== requestId) return;
      const errObj: SingleAlertInfo = { message: failureMessage, alertType: "error" };
      dispatch(setSingleAlertObj(errObj));
    } finally {
      if (requestIdRef.current === requestId) setIsRefreshingAfterSave(false);
    }
  };

  const handleQuickEditInvoiceSaved = () =>
    refreshSheet(
      quickEditInvoiceId,
      "Invoice was updated, but the sales sheet could not be refreshed. Reopen it to see the latest data."
    );

  const RATE_EDIT_COLUMNS: Record<
    string,
    { columnId: "agencyCommPercent" | "overrideCommPercent" | "v3CommPercent"; editMode: "rate" | "amount" }
  > = {
    agencyCommPercent: { columnId: "agencyCommPercent", editMode: "rate" },
    overrideCommPercent: { columnId: "overrideCommPercent", editMode: "rate" },
    v3CommPercent: { columnId: "v3CommPercent", editMode: "rate" },
    "agencyComm__secondary": { columnId: "agencyCommPercent", editMode: "amount" },
    "overrideComm__secondary": { columnId: "overrideCommPercent", editMode: "amount" },
    "v3NetComm__secondary": { columnId: "v3CommPercent", editMode: "amount" },
  };

  const handleFeeEdit = (
    context: unknown,
    meta: { rowIndex: number; row: ExcelSheetRow; columnId: string; label: string }
  ) => {
    const rateEditTarget = RATE_EDIT_COLUMNS[meta.columnId];
    if (rateEditTarget) {
      const rateEdit = context as FinanceHqRateEditContext | undefined;
      if (
        !rateEdit ||
        !Number.isFinite(rateEdit.paymentId) ||
        !Number.isFinite(rateEdit.agencyId) ||
        !Number.isFinite(rateEdit.invoiceId)
      ) {
        dispatch(
          setSingleAlertObj({
            message: `Unable to open ${meta.label}: missing agency details for this row.`,
            alertType: "error",
          })
        );
        return;
      }
      dispatch(fetchInvoiceAgency({ invoiceId: rateEdit.invoiceId, skipLoader: true }))
        .unwrap()
        .then((response: any) => {
          const invoiceAgencyList = response?.data || [];
          const match = invoiceAgencyList.find((item: any) => item.agencyId === rateEdit.agencyId);
          if (!match || !Number.isFinite(Number(match.invoiceAgencyId))) {
            dispatch(
              setSingleAlertObj({
                message: `Unable to open ${meta.label}: missing agency details for this row.`,
                alertType: "error",
              })
            );
            return;
          }
          setCommissionRateEdit({
            label: meta.label,
            invoiceId: rateEdit.invoiceId,
            columnId: rateEditTarget.columnId,
            editMode: rateEditTarget.editMode,
            agencyId: rateEdit.agencyId,
            paymentData: {
              paymentId: rateEdit.paymentId,
              invoiceAgencyId: Number(match.invoiceAgencyId),
              currentRate: rateEdit.currentRate,
            },
            agencyData: {
              agencyName: rateEdit.agencyName,
              invoiceAmt: rateEdit.invoiceAmt,
              invoiceNumber: meta.row.mfgInvoiceNumber !== undefined ? String(meta.row.mfgInvoiceNumber) : undefined,
              orderNumber:
                meta.row.raworderNumber !== undefined
                  ? String(meta.row.raworderNumber)
                  : meta.row.orderNumber !== undefined
                    ? String(meta.row.orderNumber)
                    : undefined,
            },
          });
        })
        .catch(() => {
          dispatch(
            setSingleAlertObj({ message: `Unable to open ${meta.label}.`, alertType: "error" })
          );
        });
      return;
    }
    const fee = context as FinanceHqFeeContext | undefined;
    if (!fee || !Number.isFinite(fee.agencyId) || !Number.isFinite(fee.paymentId)) {
      dispatch(
        setSingleAlertObj({
          message: `Unable to open ${meta.label}: missing payment/agency details for this row.`,
          alertType: "error",
        })
      );
      return;
    }
    const remainingAmount = roundMoney(fee.expectedAmount - fee.alreadyPaidAmount);
    if (remainingAmount <= 0) {
      dispatch(
        setSingleAlertObj({ message: `${meta.label} has already been paid in full.`, alertType: "success" })
      );
      return;
    }
    setFeePayout({
      label: meta.label,
      context: fee,
      expectedAmount: fee.expectedAmount,
      alreadyPaidAmount: fee.alreadyPaidAmount,
      invoiceNumber: meta.row.mfgInvoiceNumber !== undefined ? String(meta.row.mfgInvoiceNumber) : undefined,
      orderNumber:
        meta.row.raworderNumber !== undefined
          ? String(meta.row.raworderNumber)
          : meta.row.orderNumber !== undefined
            ? String(meta.row.orderNumber)
            : undefined,
      commissionObject: {
        agencyId: fee.agencyId,
        invoiceId: fee.invoiceId,
        paymentId: fee.paymentId,
        commissionAgencyType: toCommissionTypeRequestKey(allCodeTablesValuesMap, fee.commissionAgencyType),
        commissionAmount: remainingAmount,
        commissionReceivedAmount: remainingAmount,
        payoutDate: "",
        payoutComment: "",
      },
    });
  };

  const handleCloseFeePayout = () => setFeePayout(null);

  const applyFeePayoutPatch = (fee: FinanceHqFeeContext, payoutDate: string) => {
    setView((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => {
        if (Number(row.invoiceId) !== fee.invoiceId) return row;
        const payments = Array.isArray(row.payments) ? (row.payments as any[]) : [];
        const nextPayments = payments.map((subRow: any) => {
          if (fee.commissionAgencyType === "V3") {
            if (subRow?._v3Fee?.paymentId !== fee.paymentId) return subRow;
            writeFeePayoutCacheEntry(feePayoutCacheKey(fee.invoiceId, fee.paymentId, "V3", fee.agencyId), {
              percent: subRow.v3CommPercent,
              amount: subRow.v3NetComm,
              status: "PAID",
              date: payoutDate,
            });
            return { ...subRow, v3CommStatus: "PAID", v3ReceivedDate: payoutDate };
          }
          if (fee.commissionAgencyType === "AGENCY") {
            if (subRow?._agencyFee?.paymentId !== fee.paymentId) return subRow;
            writeFeePayoutCacheEntry(feePayoutCacheKey(fee.invoiceId, fee.paymentId, "AGENCY", fee.agencyId), {
              percent: subRow.agencyCommPercent,
              amount: subRow.agencyComm,
              status: "PAID",
              date: payoutDate,
            });
            return { ...subRow, agencyCommStatus: "PAID", agencyPaidDate: payoutDate };
          }
          const overrides = Array.isArray(subRow.overrides) ? subRow.overrides : [];
          let changed = false;
          const nextOverrides = overrides.map((overrideRow: any) => {
            if (
              overrideRow?._overrideFee?.paymentId !== fee.paymentId ||
              overrideRow?._overrideFee?.agencyId !== fee.agencyId
            ) {
              return overrideRow;
            }
            changed = true;
            writeFeePayoutCacheEntry(feePayoutCacheKey(fee.invoiceId, fee.paymentId, "OVERRIDE", fee.agencyId), {
              percent: overrideRow.overrideCommPercent,
              amount: overrideRow.overrideComm,
              status: "PAID",
              date: payoutDate,
            });
            return { ...overrideRow, overrideCommStatus: "PAID", overridePaidDate: payoutDate };
          });
          return changed ? { ...subRow, overrides: nextOverrides } : subRow;
        });
        return { ...row, payments: nextPayments };
      }),
    }));
    setHighlightInvoiceId(fee.invoiceId);
    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = window.setTimeout(() => setHighlightInvoiceId(null), 2500);
  };

  const handleConfirmFeePayout = async (data: AddCommissionRequest) => {
    const fee = feePayout?.context ?? null;
    try {
      await dispatch(addCommission({ ...data, skipLoader: true })).unwrap();
      dispatch(setSingleAlertObj({ message: `${feePayout?.label || "Service fee"} paid.`, alertType: "success" }));
      if (fee) applyFeePayoutPatch(fee, data.payoutDate || "");
    } catch {
      dispatch(
        setSingleAlertObj({
          message: `Unable to pay ${feePayout?.label || "service fee"}.`,
          alertType: "error",
        })
      );
    }
  };

  const handleCloseCommissionRateEdit = () => setCommissionRateEdit(null);

  /**
   * The invoice-list endpoint the sheet reads from doesn't reflect a commission
   * rate change as fast as the invoice-detail endpoint does, so a re-fetch right
   * after saving can redisplay the stale rate. Patch the row locally with the
   * value we know we just saved instead of relying on a re-fetch.
   */
  const applyCommissionRatePatch = (
    rateEdit: NonNullable<typeof commissionRateEdit>,
    newRate: number
  ) => {
    setView((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => {
        if (Number(row.invoiceId) !== rateEdit.invoiceId) return row;
        const payments = Array.isArray(row.payments) ? (row.payments as any[]) : [];
        const nextPayments = payments.map((subRow: any) => {
          if (rateEdit.columnId === "v3CommPercent") {
            if (subRow?._v3RateEdit?.paymentId !== rateEdit.paymentData.paymentId) return subRow;
            const netPaymentAmount = parseExcelNumber(subRow.netInvPmtAmt);
            return {
              ...subRow,
              v3CommPercent: formatExcelPercent(newRate),
              v3NetComm: formatExcelCurrency((netPaymentAmount * newRate) / 100),
              _v3RateEdit: { ...subRow._v3RateEdit, currentRate: newRate },
            };
          }
          if (rateEdit.columnId === "agencyCommPercent") {
            if (subRow?._agencyRateEdit?.paymentId !== rateEdit.paymentData.paymentId) return subRow;
            const netPaymentAmount = parseExcelNumber(subRow.netInvPmtAmt);
            return {
              ...subRow,
              agencyCommPercent: formatExcelPercent(newRate),
              agencyComm: formatExcelCurrency((netPaymentAmount * newRate) / 100),
              _agencyRateEdit: { ...subRow._agencyRateEdit, currentRate: newRate },
            };
          }
          const overrides = Array.isArray(subRow.overrides) ? subRow.overrides : [];
          const netPaymentAmount = parseExcelNumber(subRow.netInvPmtAmt);
          let changed = false;
          const nextOverrides = overrides.map((overrideRow: any) => {
            if (
              overrideRow?._overrideRateEdit?.paymentId !== rateEdit.paymentData.paymentId ||
              overrideRow?._overrideRateEdit?.agencyId !== rateEdit.agencyId
            ) {
              return overrideRow;
            }
            changed = true;
            return {
              ...overrideRow,
              overrideCommPercent: formatExcelPercent(newRate),
              overrideComm: formatExcelCurrency((netPaymentAmount * newRate) / 100),
              _overrideRateEdit: { ...overrideRow._overrideRateEdit, currentRate: newRate },
            };
          });
          return changed ? { ...subRow, overrides: nextOverrides } : subRow;
        });
        return { ...row, payments: nextPayments };
      }),
    }));
    setHighlightInvoiceId(rateEdit.invoiceId);
    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = window.setTimeout(() => setHighlightInvoiceId(null), 2500);
  };

  const handleCommissionRateUpdate = async (data: any) => {
    const rateEdit = commissionRateEdit;
    if (data?.noChange) {
      dispatch(setSingleAlertObj({ message: `${rateEdit?.label || "Service rate"} unchanged.`, alertType: "success" }));
      setCommissionRateEdit(null);
      return;
    }
    try {
      const response: any = await dispatch(editCommissionRateAction({ ...data, skipLoader: true }));
      if (response?.payload?.data?.code === "V3OK") {
        dispatch(setSingleAlertObj({ message: `${rateEdit?.label || "Service rate"} updated.`, alertType: "success" }));
        if (rateEdit) {
          applyCommissionRatePatch(rateEdit, Number(data.newCommissionRate));
        }
      } else {
        // The API rejected the request (validation, permission, etc.) — surface why
        // instead of a generic message, and log the full response for debugging.
        console.error("editCommissionRateAction failed", { data, response });
        const backendMessage =
          response?.payload?.data?.message ||
          response?.payload?.message ||
          response?.payload?.error ||
          (Array.isArray(response?.payload?.errors) ? response.payload.errors.join(", ") : undefined) ||
          response?.error?.message;
        dispatch(
          setSingleAlertObj({
            message: backendMessage
              ? `Unable to update ${rateEdit?.label || "service rate"}: ${backendMessage}`
              : `Unable to update ${rateEdit?.label || "service rate"}.`,
            alertType: "error",
          })
        );
      }
    } catch (error) {
      console.error("editCommissionRateAction threw", { data, error });
      dispatch(
        setSingleAlertObj({ message: `Unable to update ${rateEdit?.label || "service rate"}.`, alertType: "error" })
      );
    } finally {
      setCommissionRateEdit(null);
    }
  };

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const handleClose = () => {
    if (window.opener) {
      window.close();
      return;
    }
    navigate("/commission_list");
  };

  const isDarkMode = theme.palette.mode === "dark";
  const FILTER_FIELD_HEIGHT = "34px";
  const filterBarSx = {
    ...filterPanelSx,
    borderRadius: 0,
    boxShadow: "none",
    border: "none",
    borderBottom: `1px solid ${isDarkMode ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
    backgroundColor: isDarkMode ? "#12161e" : "#f8fafc",
    backdropFilter: "none",
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root":
      {
        ...(filterPanelSx as any)[
          "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root"
        ],
        minHeight: `${FILTER_FIELD_HEIGHT} !important`,
        height: `${FILTER_FIELD_HEIGHT} !important`,
        borderRadius: "12px !important",
      },
  };

  const handleOpenFilters = (event: React.MouseEvent<HTMLElement>) => setFiltersAnchorEl(event.currentTarget);
  const handleCloseFilters = () => setFiltersAnchorEl(null);
  const handleApplyFiltersFromPopover = () => {
    handleSearchFilters();
    setFiltersAnchorEl(null);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    (Object.keys(FILTER_DEFAULT_VALUES) as (keyof SheetFilterFormValues)[]).forEach((key) => {
      const value = appliedFilters[key];
      if (!isAllOrEmpty(value as string | number)) count += 1;
    });
    if (appliedFilters.invoiceDateFrom || appliedFilters.invoiceDateTo) count += 1;
    if (appliedFilters.dueDateFrom || appliedFilters.dueDateTo) count += 1;
    if (appliedFilters.v3ReceivedFromDate || appliedFilters.v3ReceivedToDate) count += 1;
    if (appliedFilters.agencyPaidFromDate || appliedFilters.agencyPaidToDate) count += 1;
    if (appliedFilters.ovrPaidFromDate || appliedFilters.ovrPaidToDate) count += 1;
    return count;
  }, [appliedFilters]);

  const FILTER_FIELDS: { key: string; node: React.ReactNode; wide?: boolean }[] = [
    {
      key: "mfgInvoiceNumber",
      node: (
        <CustomTextField
          control={control}
          controllerName="mfgInvoiceNumber"
          name="mfgInvoiceNumber"
          label="Invoice ID"
          placeholder="Enter invoice ID"
          size="small"
          onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && handleSearchFilters()}
        />
      ),
    },
    {
      key: "orderNumber",
      node: (
        <CustomTextField
          control={control}
          controllerName="orderNumber"
          name="orderNumber"
          label="Order ID"
          placeholder="Enter order ID"
          size="small"
          onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && handleSearchFilters()}
        />
      ),
    },
    {
      key: "practiceId",
      node: <CustomDropdownField control={control} name="practiceId" label="Practice" placeholderText="Select practice" options={practiceOptions} size="small" zIndex={2000} />,
    },
    {
      key: "patientId",
      node: <CustomDropdownField control={control} name="patientId" label="Patient" placeholderText="Select patient" options={patientOptions} size="small" zIndex={2000} />,
    },
    {
      key: "mfgIds",
      node: <CustomDropdownField control={control} name="mfgIds" label="Manufacturer" placeholderText="Select manufacturer" options={manufacturerOptions} size="small" zIndex={2000} />,
    },
    {
      key: "productId",
      node: <CustomDropdownField control={control} name="productId" label="Product" placeholderText="Select product" options={productOptions} size="small" zIndex={2000} />,
    },
    {
      key: "invoiceDateRange",
      node: (
        <CustomDateRange
          label="Invoice Date Range"
          control={control}
          controllerName="invoiceDateRange"
          onChange={handleInvoiceDateRangeChange}
          zIndex={2000}
        />
      ),
    },
    {
      key: "agencyId",
      node: <CustomDropdownField control={control} name="agencyId" label="Agency" placeholderText="Select agency" options={agencyOptions} size="small" zIndex={2000} />,
    },
    {
      key: "invoiceStatus",
      node: (
        <CustomDropdownField control={control} name="invoiceStatus" label="Invoice Status" placeholderText="Select status" options={statusDropdownOptions} sortOptions={false} size="small" zIndex={2000} />
      ),
    },
    {
      key: "v3CommStatus",
      node: (
        <CustomDropdownField control={control} name="v3CommStatus" label="V3 Pay Status" placeholderText="Select V3 Svc Fee status" options={v3PayStatusOptions} sortOptions={false} size="small" zIndex={2000} />
      ),
    },
    {
      key: "agencyCommStatus",
      node: (
        <CustomDropdownField control={control} name="agencyCommStatus" label="AGY Pay Status" placeholderText="Select AGY Svc Fee status" options={agyPayStatusOptions} sortOptions={false} size="small" zIndex={2000} />
      ),
    },
    {
      key: "ovrCommStatus",
      node: (
        <CustomDropdownField control={control} name="ovrCommStatus" label="OVR Pay Status" placeholderText="Select OVR Svc Fee status" options={agyPayStatusOptions} sortOptions={false} size="small" zIndex={2000} />
      ),
    },
    {
      key: "v3ReceivedDateRange",
      node: (
        <CustomDateRange
          label="V3 Paid Date"
          control={control}
          controllerName="v3ReceivedDateRange"
          onChange={handleV3ReceivedDateRangeChange}
          zIndex={2000}
        />
      ),
    },
    {
      key: "agencyPaidDateRange",
      node: (
        <CustomDateRange
          label="AGY Paid Date"
          control={control}
          controllerName="agencyPaidDateRange"
          onChange={handleAgencyPaidDateRangeChange}
          zIndex={2000}
        />
      ),
    },
    {
      key: "ovrPaidDateRange",
      node: (
        <CustomDateRange
          label="OVR Paid Date"
          control={control}
          controllerName="ovrPaidDateRange"
          onChange={handleOvrPaidDateRangeChange}
          zIndex={2000}
        />
      ),
    },
    {
      key: "commPeriod",
      node: (
        <CustomMonthYearPicker
          label="Pay Period"
          control={control}
          controllerName="commPeriod"
          name="commPeriod"
          onDateChange={handleCommPeriodChange}
          onClear={handleClearCommPeriod}
        />
      ),
    },
    {
      key: "serialNumber",
      node: (
        <CustomTextField
          control={control}
          controllerName="serialNumber"
          name="serialNumber"
          label="Serial Number"
          placeholder="Enter serial number"
          size="small"
          onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && handleSearchFilters()}
        />
      ),
    },
    {
      key: "usageStatus",
      node: (
        <CustomDropdownField control={control} name="usageStatus" label="Usage Status" placeholderText="Select usage status" options={usageStatusDropdownOptions} sortOptions={false} size="small" zIndex={2000} />
      ),
    },
    {
      key: "actions",
      wide: true,
      node: (
        <Box sx={{ display: "flex", gap: 1}}>
          {activeFilterCount > 0 ? (
            <MuiButton
              variant="outlined"
              color="info"
              onClick={() => {
                handleClearFilters();
                handleCloseFilters();
              }}
              sx={{ ...outlineBtnSx, textTransform: "none", height: FILTER_FIELD_HEIGHT, whiteSpace: "nowrap" }}
            >
              Clear Filters
            </MuiButton>
          ) : null}
          
          <MuiButton
           startIcon={
              <SearchOutlined
                htmlColor="#ffffff"
                sx={{
                  fontSize: 16,
                  color: "#ffffff !important",
                }}
              />
            }
            onClick={handleApplyFiltersFromPopover}
            sx={{ ...primaryActionBtnSx, textTransform: "none", height: FILTER_FIELD_HEIGHT, whiteSpace: "nowrap" }}
          >
            Search
          </MuiButton>
          <MuiButton
            variant="outlined"
            color="info"
            onClick={handleCloseFilters}
            sx={{ ...outlineBtnSx, textTransform: "none", height: FILTER_FIELD_HEIGHT, whiteSpace: "nowrap" }}
          >
            Cancel
          </MuiButton>
          
        </Box>
      ),
    },
  ];

  const compactFieldLabelSx = {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)",
    mb: 0.4,
    height: "14px",
    lineHeight: "14px",
  };

  const quickFilterField = (label: string, node: React.ReactNode, width = 150) => (
    <Box sx={{ flex: `1 1 ${width}px`, minWidth: width }}>
      <Typography sx={{ ...compactFieldLabelSx, whiteSpace: "nowrap" }}>{label}</Typography>
      {node}
    </Box>
  );

  const filterBar = (exportButton: React.ReactNode, controls: React.ReactNode) => (
    <Box sx={{ position: "relative", zIndex: 20, overflow: "visible", width: "100%" }}>
    <Box sx={{ ...filterBarSx, borderRadius: 0, width: "100%", py: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          gap: 1.25,
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "hidden",
          pb: 0.5,
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isDarkMode
              ? "rgba(255,255,255,0.18)"
              : "rgba(0,0,0,0.18)",
            borderRadius: 3,
          },
        }}
      >
        {quickFilterField(
          "Search",
          <CustomTextField
            control={control}
            controllerName="search"
            name="search"
            placeholder="Search..."
            size="small"
            onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && handleSearchFilters()}
          />,
          180
        )}
        {quickFilterField(
          "Practice",
          <CustomDropdownField control={control} name="practiceId" placeholderText="Select practice" options={practiceOptions} size="small" />
        )}
        {quickFilterField(
          "Manufacturer",
          <CustomDropdownField control={control} name="mfgIds" placeholderText="Select manufacturer" options={manufacturerOptions} size="small" />
        )}
        {quickFilterField(
          "Invoice Date",
          <CustomDateRange control={control} controllerName="invoiceDateRange" onChange={handleInvoiceDateRangeChange} zIndex={2000} />
        )}
        {quickFilterField(
          "Due Date",
          <CustomDateRange control={control} controllerName="dueDateRange" onChange={handleDueDateRangeChange} zIndex={2000} />
        )}
        {quickFilterField(
          "Status",
          <CustomDropdownField control={control} name="invoiceStatus" placeholderText="Select status" options={statusDropdownOptions} sortOptions={false} size="small" />,
          120
        )}
        {activeFilterCount > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              flexShrink: 0,
              height: "48px",
              mt:0.5
            }}
          >
            <MuiButton
              variant="outlined"
              color="info"
              onClick={handleClearFilters}
              sx={{
                ...outlineBtnSx,
                textTransform: "none",
                minHeight: 0,
                height: FILTER_FIELD_HEIGHT,
                whiteSpace: "nowrap",
              }}
            >
              Clear Filters
            </MuiButton>
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            flexShrink: 0,
            height: "48px",
            mt:0.5
          }}
        >
          <MuiButton
            startIcon={
              <SearchOutlined
                htmlColor="#ffffff"
                sx={{
                  fontSize: 16,
                  color: "#ffffff !important",
                }}
              />
            }
            onClick={handleSearchFilters}
            sx={{
              ...primaryActionBtnSx,
              textTransform: "none",
              minHeight: 0,
              height: FILTER_FIELD_HEIGHT,
              whiteSpace: "nowrap",
            }}
          >
            Search
          </MuiButton>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Box
            role="button"
            tabIndex={0}
            aria-label="Advance filters"
            onClick={handleOpenFilters}
            onKeyDown={(e: React.KeyboardEvent) => (e.key === "Enter" || e.key === " ") && handleOpenFilters(e as unknown as React.MouseEvent<HTMLElement>)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1px",
              px: 0.75,
              py: 0.5,
              minWidth: 52,
              borderRadius: 1,
              cursor: "pointer",
              userSelect: "none",
              color: activeFilterCount > 0 ? "#00B8D9" : outlineBtnSx.color,
              "&:hover": { bgcolor: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)" },
            }}
          >
            <FilterAltOutlinedIcon sx={{ fontSize: 20 }} />
            <Box sx={{ fontSize: 11, lineHeight: 1.15, textAlign: "center" }}>
              Advance{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", fontSize: 11, lineHeight: 1.15 }}>
              Filters
              <KeyboardArrowDownIcon
                sx={{ fontSize: 16, transform: filtersOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }}
              />
            </Box>
          </Box>
          {exportButton}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>{controls}</Box>
        </Box>
      </Box>
    </Box>
    <Popover
      open={filtersOpen}
      anchorEl={filtersAnchorEl}
      onClose={handleCloseFilters}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{ zIndex: 1600 }}
      PaperProps={{
        sx: {
          ...filterPanelSx,
          mt: 1,
          width: 1040,
          maxWidth: "95vw",
          boxShadow: isDarkMode ? "0 16px 40px rgba(0,0,0,0.45)" : "0 16px 40px rgba(15,23,42,0.12)",
        },
      }}
    >
      <Grid container spacing={2} alignItems="flex-end">
        {FILTER_FIELDS.map((field) => (
          <Grid item md={field.wide ? 6 : 3} xs={12} key={field.key}>
            {field.node}
          </Grid>
        ))}
      </Grid>
    </Popover>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <ExcelSheetPopup
        open
        onClose={handleClose}
        forceFullScreen
        refreshing={isRefreshingAfterSave}
        highlightRowKey={highlightInvoiceId}
        toolbar={filterBar}
        onCellPopupEdit={handleCellPopupEdit}
        getRowActions={getRowActions}
        onFeeEdit={handleFeeEdit}
        getSelectionActions={getSelectionActions}
        columns={financeHqSalesSheetColumns}
        rows={displayRows}
        loading={view.loading}
        emptyText="No Invoice(s) Found"
        exportFileName={FINANCE_HQ_SALES_SHEET_EXPORT_FILE}
        maxExportRecords={FINANCE_HQ_EXCEL_MAX_EXPORT_RECORDS}
        showTotals
        totalsValues={view.summary}
        totalRecords={view.totalRecords}
        onLoadMore={handleLoadMore}
        loadMoreLoading={view.loadMoreLoading}
        loadMoreSize={excelLoadMoreLimit}
        onFetchAllRows={handleFetchAllRows}
        clearColumnFiltersSignal={filterSearchTick}
        savableColumnIds={FINANCE_HQ_SALES_SHEET_SAVABLE_COLUMN_IDS}
        onSaveRow={handleSaveRow}
      />
      <Dialog
        open={quickEditInvoiceId !== null}
        onClose={handleCloseQuickEditInvoice}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        sx={{ zIndex: 1600 }}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        {quickEditInvoiceId !== null ? (
          <>
            <Box sx={headerSx}>
              <Box sx={iconBoxSx(tokens.accent)}>
                <ReceiptLongOutlinedIcon sx={{ fontSize: "1.25rem", color: tokens.accent }} />
              </Box>
              <Box sx={{ flex: 1, pr: 4 }}>
                <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                  Invoice Details
                </Typography>
                 <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                  Review selected grafts, service fees, and update invoice details.
                </Typography>
              </Box>
              <IconButton onClick={handleCloseQuickEditInvoice} size="small" sx={closeIconSx} aria-label="Close">
                <CloseIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Box>
            <Box sx={bodySx}>
              <EditInvoice
                key={quickEditInvoiceId}
                ref={quickEditInvoiceRef}
                invoiceIdOverride={quickEditInvoiceId}
                onRequestClose={handleCloseQuickEditInvoice}
                onSaved={handleQuickEditInvoiceSaved}
                hideHeader
                compact
                highlightFieldName={quickEditHighlightField}
              />
            </Box>
            <Box sx={footerSx}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => quickEditInvoiceRef.current?.submit()}
                  sx={{
                    ...primaryBtnSx,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.8,
                    px: 3,
                    py: 1.2,
                    border: "none",
                    cursor: "pointer",
                    color: "#fff",
                    order: { xs: 2, sm: 1 },
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: "1.1rem" }} />
                  Update Invoice
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleCloseQuickEditInvoice}
                  sx={{
                    ...cancelBtnSx,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.8,
                    px: 3,
                    py: 1.2,
                    border: "1px solid",
                    cursor: "pointer",
                    order: { xs: 1, sm: 2 },
                  }}
                >
                  Cancel
                </Box>
              </Box>
            </Box>
          </>
        ) : null}
      </Dialog>
      <Dialog
        open={recordPaymentContext !== null}
        onClose={handleCloseRecordPayment}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        sx={{ zIndex: 1600 }}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        {recordPaymentContext !== null ? (
          <>
            <Box sx={headerSx}>
              <Box sx={iconBoxSx(tokens.accent)}>
                <PaymentsOutlinedIcon sx={{ fontSize: "1.25rem", color: tokens.accent }} />
              </Box>
              <Box sx={{ flex: 1, pr: 4 }}>
                <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                  Record Payment
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                  Record a payment for the selected invoice.
                </Typography>
              </Box>
              <IconButton onClick={handleCloseRecordPayment} size="small" sx={closeIconSx} aria-label="Close">
                <CloseIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Box>
            <Box sx={bodySx}>
              <AddPaymentDetail
                key={`${recordPaymentContext.invoiceId}-${recordPaymentContext.paymentDate}-${recordPaymentContext.paidAmount}`}
                invoiceIdOverride={recordPaymentContext.invoiceId}
                initialPaymentDate={recordPaymentContext.paymentDate}
                initialPaidAmount={recordPaymentContext.paidAmount}
                onRequestClose={handleCloseRecordPayment}
                onSaved={handleRecordPaymentSaved}
                compact
                highlightFieldName={recordPaymentContext.highlightField}
              />
            </Box>
          </>
        ) : null}
      </Dialog>
      <Dialog
        open={overridePayoutInvoices !== null}
        onClose={handleCloseOverridePayout}
        fullWidth
        maxWidth="lg"
        fullScreen={isMobile}
        sx={{ zIndex: 1600 }}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        {overridePayoutInvoices !== null ? (
          <>
            <Box sx={headerSx}>
              <Box sx={iconBoxSx(tokens.accent)}>
                <PaymentsOutlinedIcon sx={{ fontSize: "1.25rem", color: tokens.accent }} />
              </Box>
              <Box sx={{ flex: 1, pr: 4 }}>
                <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                  Record Payout
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                  Pay the override service fees for the selected invoices.
                </Typography>
              </Box>
              <IconButton onClick={handleCloseOverridePayout} size="small" sx={closeIconSx} aria-label="Close">
                <CloseIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Box>
            <Box sx={bodySx}>
              <RecordPayoutOverrideForm
                selectedInvoices={overridePayoutInvoices}
                onCancel={handleCloseOverridePayout}
                onRecorded={handleOverridePayoutRecorded}
                overlayZIndex={1600}
              />
            </Box>
          </>
        ) : null}
      </Dialog>
      <Dialog
        open={agencyPayoutInvoices !== null}
        onClose={handleCloseAgencyPayout}
        fullWidth
        maxWidth="lg"
        fullScreen={isMobile}
        sx={{ zIndex: 1600 }}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        {agencyPayoutInvoices !== null ? (
          <>
            <Box sx={headerSx}>
              <Box sx={iconBoxSx(tokens.accent)}>
                <PaymentsOutlinedIcon sx={{ fontSize: "1.25rem", color: tokens.accent }} />
              </Box>
              <Box sx={{ flex: 1, pr: 4 }}>
                <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                  Record Payout
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                  Pay the agency service fees for the selected invoices.
                </Typography>
              </Box>
              <IconButton onClick={handleCloseAgencyPayout} size="small" sx={closeIconSx} aria-label="Close">
                <CloseIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Box>
            <Box sx={bodySx}>
              <RecordPayoutForm
                selectedInvoices={agencyPayoutInvoices}
                onCancel={handleCloseAgencyPayout}
                onRecorded={handleAgencyPayoutRecorded}
                overlayZIndex={1600}
              />
            </Box>
          </>
        ) : null}
      </Dialog>
      <Dialog
        open={creditMemoContext !== null}
        onClose={handleCloseCreditMemo}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        sx={{ zIndex: 1600 }}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        {creditMemoContext !== null ? (
          <>
            <Box sx={headerSx}>
              <Box sx={iconBoxSx(tokens.accent)}>
                <CreditCardIcon sx={{ fontSize: "1.25rem", color: tokens.accent }} />
              </Box>
              <Box sx={{ flex: 1, pr: 4 }}>
                <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                  Record Credit Memo
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                  Please select items to record a credit memo for the selected invoice.
                </Typography>
              </Box>
              <IconButton onClick={handleCloseCreditMemo} size="small" sx={closeIconSx} aria-label="Close">
                <CloseIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Box>
            <Box sx={bodySx}>
              <RecordCreditMemo
                key={creditMemoContext.invoiceId}
                invoiceIdOverride={creditMemoContext.invoiceId}
                initialInvNumber={creditMemoContext.invNumber}
                initialInvDate={creditMemoContext.invDate}
                initialPractice={creditMemoContext.practice}
                initialAgency={creditMemoContext.agency}
                initialMfr={creditMemoContext.mfr}
                onRequestClose={handleCloseCreditMemo}
                onSaved={handleCreditMemoSaved}
                compact
              />
            </Box>
          </>
        ) : null}
      </Dialog>
      {stripePaymentInvoice ? (
        <StripePaymentTypePopup
          open
          onClose={handleCloseStripePayment}
          invoiceData={stripePaymentInvoice}
          createCheckoutSession={handleStripeCheckout}
        />
      ) : null}
      {showPaymentInstructionPdf ? (
        <PDFViewer
          isOpen={showPaymentInstructionPdf !== null}
          onClose={() => setShowPaymentInstructionPdf(null)}
          title={showPaymentInstructionPdf.noteName}
          fileName={showPaymentInstructionPdf.fileName}
          base64String={pdfContent || ""}
        />
      ) : null}
      {lateFeeContext ? (
        <LateFeePopup
          open
          onClose={handleCloseLateFee}
          onConfirm={handleConfirmLateFee}
          lateFeeReceivedAmount={lateFeeContext.lateFee}
          currency="$"
          lateFeeAmountLabel="Outstanding Late Fee"
          zIndex={1600}
        />
      ) : null}
      {feePayout ? (
        <CommissionPayoutPopup
          open
          onClose={handleCloseFeePayout}
          onConfirm={handleConfirmFeePayout}
          title={feePayout.label}
          commissionAmountLabel={`${feePayout.label} Amount`}
          commissionObject={feePayout.commissionObject}
          expectedAmount={feePayout.expectedAmount}
          alreadyPaidAmount={feePayout.alreadyPaidAmount}
          invoiceNumber={feePayout.invoiceNumber}
          orderNumber={feePayout.orderNumber}
          zIndex={1600}
        />
      ) : null}
      <EditCommissionPopup
        open={commissionRateEdit !== null}
        handleClose={handleCloseCommissionRateEdit}
        mode="commission-details"
        uiVariant="billing"
        showReasonField
        disableAmountField={commissionRateEdit?.editMode !== "amount"}
        disableRateField={commissionRateEdit?.editMode === "amount"}
        paymentData={commissionRateEdit?.paymentData ?? null}
        agencyData={commissionRateEdit?.agencyData ?? null}
        onCommissionRateUpdate={handleCommissionRateUpdate}
        zIndex={1600}
      />
    </Box>
  );
};

export default FinanceHqSalesSheetPage;
